// ----------------------------------------------------------------------

const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
};

// ----------------------------------------------------------------------

export const paths = {
  faqs: '/faqs',
  minimalStore: 'https://mui.com/store/items/minimal-dashboard/',
  // AUTH
  auth: {
    signIn: `${ROOTS.AUTH}/sign-in`,
    signUp: `${ROOTS.AUTH}/sign-up`,
    confirm: `${ROOTS.AUTH}/confirm`,
    forgotPassword: `${ROOTS.AUTH}/forgot-password`,
    resetPassword: `${ROOTS.AUTH}/reset-password`,
    onboarding: '/onboarding',
  },
  // INVITATION
  acceptInvitation: (token: string) => `/accept-invitation/${token}`,
  // The internal work surface: the production queue and the tickets on it. Its
  // own short top-level area rather than a branch of /dashboard, because a
  // NexMail employee has no organization to prefix with -- the brand comes from
  // the ticket (WG-84). Engagement config and its metrics live in admin-skidog.
  w: {
    root: '/w',
    queue: '/w/queue',
    ticket: (ticketId: string) => `/w/tickets/${ticketId}`,
    capacity: '/w/capacity',
    // The same inbox as `dashboard.notifications`, reachable without an
    // organization -- an employee has none, so the /dashboard form is gated
    // shut for them.
    notifications: '/w/notifications',
  },
  // DASHBOARD
  dashboard: {
    root: ROOTS.DASHBOARD,
    // Tenant-prefixed (path-based) destinations. Org + client live in the URL,
    // so each tab is isolated and deep links carry their tenant. Pass the
    // active ids (from useOrganization). The unprefixed helpers below are kept
    // for the legacy redirect, which forwards old paths into this prefix.
    tenantRoot: (orgId: string, clientId: string) =>
      `${ROOTS.DASHBOARD}/o/${orgId}/c/${clientId}`,
    tenant: (orgId: string, clientId: string) => {
      const base = `${ROOTS.DASHBOARD}/o/${orgId}/c/${clientId}`;
      return {
        home: base,
        templates: `${base}/templates`,
        // One surface for every channel. `type` is omitted for email so the
        // default URL stays clean and matches what the route treats as default.
        create: (type?: 'email' | 'sms' | 'mms') =>
          type && type !== 'email' ? `${base}/create?type=${type}` : `${base}/create`,
        // Deprecated alias for `create()`. Points at the new path so existing
        // callers land in the right place without a redirect hop; prefer
        // `create()` in new code.
        emailCreate: `${base}/create`,
        emailNew: `${base}/emails/new`,
        emailEdit: (templateId: string) => `${base}/emails/${templateId}/edit`,
        emailChat: (templateId: string) => `${base}/emails/${templateId}/chat`,
        sections: `${base}/sections`,
        sectionEdit: (id: string) => `${base}/sections/${id}`,
        sms: `${base}/sms`,
        smsEdit: (smsMessageId: string) => `${base}/sms/${smsMessageId}`,
        mediaLibrary: `${base}/media-library`,
        analytics: `${base}/analytics`,
        analyticsAudit: `${base}/analytics/audit`,
        flows: `${base}/flows`,
        flowDetail: (flowId: string) => `${base}/flows/${flowId}`,
        flowEdit: (flowId: string) => `${base}/flows/${flowId}/edit`,
        flowCreate: `${base}/flows/create`,
        signupForms: `${base}/signup-forms`,
        signupFormEdit: (formId: string) => `${base}/signup-forms/${formId}`,
        socialAds: `${base}/social-ads`,
        socialAdsCreate: `${base}/social-ads/create`,
        socialAdsEdit: (adId: string) => `${base}/social-ads/${adId}/edit`,
        workspaces: `${base}/workspaces`,
        imageGenerator: `${base}/image-generator`,
        campaignCalendar: `${base}/calendar`,
        // White Glove, the managed service. The landing page is the brand's own
        // dashboard at `/c/:client`; these are the screens it opens. The brief
        // batch and the design review hang off the route without being tabs.
        // `whiteGlove` itself redirects to Tasks, which is what a customer
        // opens the service for.
        whiteGlove: `${base}/white-glove`,
        whiteGloveTasks: `${base}/white-glove/tasks`,
        whiteGloveCalendar: `${base}/white-glove/calendar`,
        whiteGloveMonth: `${base}/white-glove/month`,
        // The five-step intake (F1). A tab of its own rather than a takeover,
        // because two of its steps send the customer into settings and back.
        whiteGloveIntake: `${base}/white-glove/intake`,
        // Asking for the two free designs from inside the product, for a brand
        // that never took them at signup.
        whiteGloveTry: `${base}/white-glove/try`,
        // The schedule after setup. The wizard has always said "you can change
        // this later"; this is where later happens.
        whiteGloveSchedule: `${base}/white-glove/schedule`,
        // One batch of briefs, addressed the way the server addresses it
        // (`briefs?batch=<planId>`) so a task row and a notification land on
        // the same page.
        whiteGloveBriefs: (planId?: string) =>
          planId ? `${base}/white-glove/briefs?batch=${planId}` : `${base}/white-glove/briefs`,
        whiteGloveTicket: (ticketId: string) => `${base}/white-glove/tickets/${ticketId}`,
        // Hidden (no nav item) -- reachable by direct URL only.
        audit: `${base}/audit`,
      };
    },
    // Settings live under the ORG prefix (org-scoped). The brand detail appends
    // its own `/client/brand/$clientId` when a client is selected.
    orgSettings: (orgId: string) => {
      const base = `${ROOTS.DASHBOARD}/o/${orgId}/settings`;
      return {
        root: base,
        profile: `${base}/profile`,
        account: `${base}/account`,
        organization: {
          root: `${base}/organization`,
          general: `${base}/organization/general`,
          team: `${base}/organization/team`,
          clients: `${base}/organization/clients`,
          billing: `${base}/organization/billing`,
          api: `${base}/organization/api`,
        },
        client: {
          root: `${base}/client`,
          brand: `${base}/client/brand`,
          brandProfiles: `${base}/client/brand/profiles`,
          brandIntegrations: `${base}/client/brand/integrations`,
          brandDetail: (clientId: string) => `${base}/client/brand/${clientId}`,
          clientIntegrations: (clientId: string) =>
            `${base}/client/brand/${clientId}/integrations`,
          clientProducts: (clientId: string) => `${base}/client/brand/${clientId}/products`,
          clientKnowledge: (clientId: string) => `${base}/client/brand/${clientId}/knowledge`,
          clientPageImports: (clientId: string) => `${base}/client/brand/${clientId}/page-imports`,
          clientIntegrationDetail: (clientId: string, provider: string) =>
            `${base}/client/brand/${clientId}/integrations/${provider}`,
        },
      };
    },
    orgClients: (orgId: string) => `${ROOTS.DASHBOARD}/o/${orgId}/clients`,
    // Cross-brand White Glove tasks (WG-54). Org-scoped with no client in the
    // path on purpose: the whole point of the view is that it spans brands, and
    // a client id in the URL would make one of them privileged.
    orgWhiteGloveTasks: (orgId: string) => `${ROOTS.DASHBOARD}/o/${orgId}/white-glove/tasks`,
    analytics: `${ROOTS.DASHBOARD}/analytics`,
    two: `${ROOTS.DASHBOARD}/two`,
    three: `${ROOTS.DASHBOARD}/three`,
    group: {
      root: `${ROOTS.DASHBOARD}/group`,
      five: `${ROOTS.DASHBOARD}/group/five`,
      six: `${ROOTS.DASHBOARD}/group/six`,
    },
    // Untenanted forms of the unified create surface. The legacy forwarder
    // prefixes these into /dashboard/o/:org/c/:client/create in one hop, so
    // they deliberately do NOT point at the old /emails/create path.
    create: `${ROOTS.DASHBOARD}/create`,
    emailCreate: `${ROOTS.DASHBOARD}/create`,
    emailEdit: (templateId: string) => `${ROOTS.DASHBOARD}/emails/${templateId}/edit`,
    emailStepper: `/emails/stepper`,
    emailCreateNew: `/emails/create-new`,
    emailGPT: `${ROOTS.DASHBOARD}/create`,
    emailVo: `/emails/email-vo`,
    aiTemplateGeneration: `/emails/ai-template-generation`,
    featureFlags: `${ROOTS.DASHBOARD}/admin/feature-flags`,
    emailCategoryAdmin: `${ROOTS.DASHBOARD}/admin/email-categories`,
    admin: `/admin`,
    adminSkidog: {
      root: `/admin-skidog`,
      dashboard: `/admin-skidog/dashboard`,
      organizations: `/admin-skidog/organizations`,
      // Template Transfers — move a generated template (whole unit) to another client
      templateTransfers: `/admin-skidog/template-transfers`,
      // Section Templates
      // AI Costs
      aiCosts: `/admin-skidog/ai-costs`,
      // Billing Plans
      plans: `/admin-skidog/plans`,
      // Social Ad Templates
      socialAdTemplates: `/admin-skidog/social-ad-templates`,
      socialAdTemplateEdit: (id: string) => `/admin-skidog/social-ad-templates/${id}`,
      // Email Documents — combined library home (Templates + Sections tabs).
      // The detail editors keep their own routes below.
      emailDocuments: `/admin-skidog/email-documents`,
      // Email Document Sections (email-document section library)
      emailDocumentSectionNew: `/admin-skidog/email-document-sections/new`,
      emailDocumentSectionEdit: (id: string) => `/admin-skidog/email-document-sections/${id}`,
      // Email Document Templates (email-document global template library)
      emailDocumentTemplateEdit: (id: string) => `/admin-skidog/email-document-templates/${id}`,
      // System Brand Defaults — fallback config used when a brand profile is sparse
      systemBrandDefaults: `/admin-skidog/system-brand-defaults`,
      // Showcase — email screenshots shown on the public auth/login pages
      showcase: `/admin-skidog/showcase`,
      // Design Inspirations — the curated corpus a new brand picks its two free
      // designs from during onboarding
      designInspirations: `/admin-skidog/design-inspirations`,
      // SAM 3 Playground — dev-mode tool for testing the inference /segment endpoint
      sam3Playground: `/admin-skidog/sam3-playground`,
      // Background Removal — side-by-side SAM 3 vs PhotoRoom on a single image
      bgRemoval: `/admin-skidog/bg-removal`,
      // Feature Flags — registry + global defaults; per-org overrides live on client detail page
      featureFlags: `/admin-skidog/feature-flags`,
      // NexMail staff roster — who works here, their role, and their brand grants
      staff: `/admin-skidog/staff`,
      // White Glove engagements — every managed-service contract, live and winding down
      whiteGlove: `/admin-skidog/white-glove`,
      // One engagement's metric catalog (WG-68). Lives here rather than on the
      // work surface because it is the same screen a lead and a platform admin
      // need, and only this area is reachable by both.
      whiteGloveEngagement: (engagementId: string) =>
        `/admin-skidog/white-glove/${engagementId}`,
      // Capacity in detail — throughput and the at-risk list behind the queue's
      // own summary (WG-65, WG-112).
      whiteGloveCapacity: `/admin-skidog/capacity`,
      // Integration tasks — every fan-out workflow's progress counter across
      // all tenants, and the only surface that shows a backfill which stopped
      // partway: a chained backfill leaves no pending jobs behind when it
      // breaks, so the queue dashboard has nothing to show.
      integrationTasks: `/admin-skidog/integration-tasks`,
      // One integration's task history, reached from the brand page.
      clientIntegration: (orgId: string, clientId: string, integrationId: string) =>
        `/admin-skidog/${orgId}/clients/${clientId}/integrations/${integrationId}`,
    },
    drafts: `${ROOTS.DASHBOARD}/drafts`,
    // The notification inbox. Deliberately org-agnostic: an inbox is scoped to
    // a person, not to a brand, and its rows can span every tenant that person
    // can reach (each row carries its own tenant in the link it deep-links to).
    notifications: `${ROOTS.DASHBOARD}/notifications`,
    // Campaign calendar (bare fallback; the legacy redirect forwards to the tenant path)
    campaignCalendar: `${ROOTS.DASHBOARD}/calendar`,
    // Flow management (sync / edit / create ESP automation flows)
    flows: `${ROOTS.DASHBOARD}/flows`,
    flowDetail: (flowId: string) => `${ROOTS.DASHBOARD}/flows/${flowId}`,
    flowEdit: (flowId: string) => `${ROOTS.DASHBOARD}/flows/${flowId}/edit`,
    flowCreate: `${ROOTS.DASHBOARD}/flows/create`,
    products: `${ROOTS.DASHBOARD}/products`,
    clients: `${ROOTS.DASHBOARD}/clients`,
    // Settings - nested structure
    settings: {
      root: `${ROOTS.DASHBOARD}/settings`,
      // Personal
      profile: `${ROOTS.DASHBOARD}/settings/profile`,
      account: `${ROOTS.DASHBOARD}/settings/account`,
      // Organization
      organization: {
        root: `${ROOTS.DASHBOARD}/settings/organization`,
        general: `${ROOTS.DASHBOARD}/settings/organization/general`,
        team: `${ROOTS.DASHBOARD}/settings/organization/team`,
        clients: `${ROOTS.DASHBOARD}/settings/organization/clients`,
        billing: `${ROOTS.DASHBOARD}/settings/organization/billing`,
      },
      // Client
      client: {
        root: `${ROOTS.DASHBOARD}/settings/client`,
        brand: `${ROOTS.DASHBOARD}/settings/client/brand`,
        brandProfiles: `${ROOTS.DASHBOARD}/settings/client/brand/profiles`,
        brandIntegrations: `${ROOTS.DASHBOARD}/settings/client/brand/integrations`,
        brandDetail: (clientId: string) => `${ROOTS.DASHBOARD}/settings/client/brand/${clientId}`,
        clientIntegrations: (clientId: string) => `${ROOTS.DASHBOARD}/settings/client/brand/${clientId}/integrations`,
        clientProducts: (clientId: string) => `${ROOTS.DASHBOARD}/settings/client/brand/${clientId}/products`,
        clientKnowledge: (clientId: string) => `${ROOTS.DASHBOARD}/settings/client/brand/${clientId}/knowledge`,
        clientPageImports: (clientId: string) => `${ROOTS.DASHBOARD}/settings/client/brand/${clientId}/page-imports`,
        clientIntegrationDetail: (clientId: string, provider: string) =>
          `${ROOTS.DASHBOARD}/settings/client/brand/${clientId}/integrations/${provider}`,
      },
    },
    help: `${ROOTS.DASHBOARD}/assets`,
    faq: `${ROOTS.DASHBOARD}/assets`,
    logout: `${ROOTS.AUTH}/sign-in`,
    testTemplates: '/test-templates',
    templates: `${ROOTS.DASHBOARD}/templates`,
    contentBlocks: `${ROOTS.DASHBOARD}/content-blocks`,
    sections: `${ROOTS.DASHBOARD}/sections`,
    sectionNew: `${ROOTS.DASHBOARD}/sections/new`,
    sectionEdit: (id: string) => `${ROOTS.DASHBOARD}/sections/${id}`,
    mediaLibrary: `${ROOTS.DASHBOARD}/media-library`,
    socialAds: `${ROOTS.DASHBOARD}/social-ads`,
    socialAdsCreate: `${ROOTS.DASHBOARD}/social-ads/create`,
    socialAdsEdit: (adId: string) => `${ROOTS.DASHBOARD}/social-ads/${adId}/edit`,
    signupForms: `${ROOTS.DASHBOARD}/signup-forms`,
    signupFormEdit: (formId: string) => `${ROOTS.DASHBOARD}/signup-forms/${formId}`,
  },
};
